import React, {useState} from 'react';
import {Modal, Button, Spinner, Alert} from 'react-bootstrap';

interface DeleteAliasModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: () => Promise<void>;
  alias: string | null;
  accountEmail: string;
}

const DeleteAliasModal: React.FC<DeleteAliasModalProps> = ({
  show,
  onHide,
  onConfirm,
  alias,
  accountEmail,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState('');

  const handleConfirm = async () => {
    setIsLoading(true);
    setApiError('');

    try {
      await onConfirm();
      handleClose();
    } catch (error: any) {
      console.error('Erreur lors de la suppression de l\'alias:', error);

      if (error.response?.data?.message) {
        setApiError(error.response.data.message);
      } else if (error.response?.status === 404) {
        setApiError('L\'alias n\'existe pas ou a déjà été supprimé');
      } else {
        setApiError('Erreur lors de la suppression de l\'alias. Veuillez réessayer');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setApiError('');
      onHide();
    }
  };

  if (!alias) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="fa-solid fa-trash me-2 text-danger"></i>
          Supprimer l'alias
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Alert variant={apiError ? 'danger' : 'warning'} className="d-flex align-items-start mb-3">
          <i className={`fa-solid ${apiError ? 'fa-circle-exclamation' : 'fa-exclamation-triangle'} fs-2 me-3`}></i>
          <div className="flex-grow-1">
            {apiError ? (
              <>
                <h6 className="mb-2">Erreur</h6>
                <p className="mb-0">{apiError}</p>
              </>
            ) : (
              <>
                <h6 className="mb-2">Attention, cette action est irréversible !</h6>
                <p className="mb-0 text-dark">
                  Vous êtes sur le point de supprimer l'alias suivant :
                </p>
              </>
            )}
          </div>
          {apiError && (
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => setApiError('')}
            ></button>
          )}
        </Alert>

        <div className="p-3 bg-light rounded mb-3">
          <div className="mb-2">
            <strong>Alias :</strong> <span className="text-primary">{alias}</span>
          </div>
          <div>
            <strong>Compte associé :</strong> <span className="text-primary">{accountEmail}</span>
          </div>
        </div>

        {!apiError && (
          <div className="alert alert-info mb-0">
            <i className="fa-solid fa-info-circle me-2"></i>
            Les emails envoyés à cet alias ne seront plus redirigés vers le compte associé.
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
          <i className="fa-solid fa-xmark me-2"></i>
          Annuler
        </Button>
        <Button variant="danger" onClick={handleConfirm} disabled={isLoading}>
          {isLoading ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              Suppression en cours...
            </>
          ) : (
            <>
              <i className="fa-solid fa-trash me-2"></i>
              Confirmer la suppression
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default DeleteAliasModal;
